import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/* Экран /activate — активация учётной записи по одноразовой ссылке: сотрудник задаёт себе
   пароль вместо диктовки одноразового. Pre-auth экран того же вида, что Login.tsx — свой
   .auth-* каркас, платформенный хром (Shell/Rail/Topbar) не рендерит. */
import { useMemo, useState } from "react";
import { Btn, Ico, OsnovaLogo, Field, InputAffix, Banner, Checklist } from "@kit-v5";
import { useT } from "../i18n/i18n";
const InputAffixTyped = InputAffix;
const BannerTyped = Banner;
function passwordRules(t) {
    return [
        { id: "len", label: t("auth.password_rules.len"), test: (v) => v.length >= 10 },
        { id: "mix", label: t("auth.password_rules.mix"), test: (v) => /\p{L}/u.test(v) && /\d/.test(v) },
    ];
}
// Честные экраны отказа — сообщение по каждому исходу, без обобщения в один "ошибка".
function honestStatus(t) {
    return {
        invalid_token: {
            title: t("auth.activate.invalid_token_title"),
            body: t("auth.activate.invalid_token_body"),
        },
        token_expired: {
            title: t("auth.activate.token_expired_title"),
            body: t("auth.activate.token_expired_body"),
        },
        token_used: {
            title: t("auth.activate.token_used_title"),
            body: t("auth.activate.token_used_body"),
        },
        weak_password: {
            title: t("auth.password.rejected_title"),
            body: t("auth.activate.weak_password_body"),
        },
        rate_limited: {
            title: t("auth.activate.rate_limited_title"),
            body: t("auth.activate.rate_limited_body"),
        },
    };
}
export default function ActivateScreen({ api, token, onSuccess }) {
    const t = useT();
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const [stage, setStage] = useState("form");
    const [error, setError] = useState(null);
    const [shown, setShown] = useState(false);
    const rules = useMemo(() => passwordRules(t), [t]);
    const ruleState = useMemo(() => Object.fromEntries(rules.map((r) => [r.id, r.test(password)])), [rules, password]);
    const rulesOk = rules.every((r) => ruleState[r.id]);
    const matchErr = confirm && confirm !== password ? t("auth.password.mismatch") : null;
    const canSubmit = !!token && rulesOk && !matchErr && !!confirm && !busy;
    async function submit() {
        if (!token || !canSubmit)
            return;
        setBusy(true);
        setError(null);
        try {
            const r = await api.activate({ token, password });
            if (r.status === "ok") {
                setStage("done");
                onSuccess?.();
            }
            else {
                setError(honestStatus(t)[r.status]);
            }
        }
        catch {
            setError({ title: t("auth.common.network_error_title"), body: t("auth.common.network_error_body") });
        }
        finally {
            setBusy(false);
        }
    }
    const noToken = !token;
    return (_jsx("div", { className: "auth-root", children: _jsxs("div", { className: "auth-layout", children: [_jsxs("aside", { className: "auth-brand", children: [_jsx("div", { className: "auth-brand-top", children: _jsx(OsnovaLogo, { size: 24 }) }), _jsx("div", { className: "auth-brand-mid", children: _jsx("h1", { children: t("auth.activate.welcome_title") }) }), _jsx("div", { className: "auth-brand-foot" })] }), _jsx("main", { className: "auth-pane", children: _jsxs("div", { className: "auth-form", children: [_jsx("div", { className: "auth-brand-compact", children: _jsx(OsnovaLogo, { size: 22 }) }), stage === "form" && (_jsxs(_Fragment, { children: [_jsxs("header", { className: "auth-head", children: [_jsx("h2", { children: t("auth.activate.heading") }), _jsx("p", { children: t("auth.activate.subtitle") })] }), (noToken || error) && (_jsx(BannerTyped, { tone: "danger", title: noToken ? t("auth.activate.invalid_token_title") : error.title, body: noToken ? t("auth.activate.no_token_body") : error.body })), _jsxs("div", { className: "auth-fields", children: [_jsx(Field, { label: t("auth.password.new_label"), children: _jsx(InputAffixTyped, { value: password, onChange: setPassword, disabled: busy || noToken, placeholder: t("auth.password.new_placeholder"), type: shown ? "text" : "password", autoComplete: "new-password", spellCheck: false, prefix: _jsx("span", { className: "auth-ico", children: Ico.key }), suffix: _jsx("button", { type: "button", className: "auth-peek" + (shown ? " is-on" : ""), disabled: busy || noToken, onClick: () => setShown((s) => !s), "aria-label": t(shown ? "auth.password.peek_hide" : "auth.password.peek_show"), children: Ico.eye }) }) }), _jsx(Field, { label: t("auth.password.confirm_label"), error: matchErr ?? undefined, children: _jsx(InputAffixTyped, { value: confirm, onChange: setConfirm, disabled: busy || noToken, invalid: !!matchErr, placeholder: t("auth.password.confirm_placeholder"), type: shown ? "text" : "password", autoComplete: "new-password", spellCheck: false, onKeyDown: (e) => { if (e.key === "Enter")
                                                        submit(); }, prefix: _jsx("span", { className: "auth-ico", children: Ico.key }) }) })] }), _jsx("div", { className: "auth-rules", children: _jsx(Checklist, { readOnly: true, items: rules.map(({ id, label }) => ({ id, label })), value: ruleState }) }), _jsx(Btn, { kind: "primary", size: "lg", loading: busy, disabled: !canSubmit, onClick: submit, children: t("auth.password.save_submit") })] })), stage === "done" && (_jsxs("div", { className: "auth-done", children: [_jsx("span", { className: "auth-done-ico", children: Ico.check }), _jsx("h2", { children: t("auth.activate.done_heading") }), _jsx("p", { children: t("auth.activate.done_body") })] }))] }) })] }) }));
}
